import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftSelect from 'components/SoftSelect';
import Grid from '@mui/material/Grid';
import SoftDatePicker from 'components/SoftDatePicker';
import SoftTypography from 'components/SoftTypography';
import axios from 'axios';

// 👇 AuthContext para saber el rol
import { AuthContext } from 'context/AuthContext';

function ModalFiltros({ show, handleClose, onApplyFilters }) {
  const { hasRole } = useContext(AuthContext);
  const isClient = hasRole('client');

  const [filters, setFilters] = useState({
    cliente: null,
    fechaDesde: null,
    fechaHasta: null,
    moneda: null,
    estado: null,
  });
  const [monedaOptions, setMonedaOptions] = useState([]);
  const [clienteOptions, setClienteOptions] = useState([]);

  const estadoOptions = [
    { value: '1', label: 'No Enviada' },
    { value: '2', label: 'Enviada' },
    { value: '3', label: 'Anulada' },
  ];

  useEffect(() => {
    if (!show) return;

    // Si es cliente, no cargamos clientes y limpiamos el filtro de cliente
    if (isClient) {
      setFilters(prev => ({ ...prev, cliente: null }));
    } else {
      const fetchClients = async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_SERVER_URL}/api/maintenance/clients/`
          );
          const data = response.data;
          setClienteOptions(
            data.map(client => ({ value: client.id, label: client.nombre }))
          );
        } catch (error) {
          console.error('Error fetching clients:', error);
        }
      };
      fetchClients();
    }

    const fetchCurrencyTypes = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/api/finance/currency-types/`
        );
        const data = response.data;
        setMonedaOptions(
          (data.results || data).map(currency => ({
            value: currency.id,
            label: currency.name,
          }))
        );
      } catch (error) {
        console.error('Error fetching currency types:', error);
      }
    };

    fetchCurrencyTypes();
  }, [show, isClient]);

  const handleApplyFilters = () => {
    const outgoing = { ...filters };
    // Seguridad extra: si es cliente, no mandamos cliente desde UI
    if (isClient) outgoing.cliente = null;

    onApplyFilters(outgoing);
    handleClose();
  };

  const handleReset = () => {
    setFilters({
      cliente: null,
      fechaDesde: null,
      fechaHasta: null,
      moneda: null,
      estado: null,
    });
  };

  const handleFechaDesdeChange = date => {
    const selectedDate = date[0];
    setFilters(prev => {
      if (selectedDate && !prev.fechaHasta) {
        const nextDay = new Date(selectedDate);
        nextDay.setDate(nextDay.getDate() + 1);
        return { ...prev, fechaDesde: selectedDate, fechaHasta: nextDay };
      }
      return { ...prev, fechaDesde: selectedDate };
    });
  };

  const handleFechaHastaChange = date => {
    const selectedDate = date[0];
    setFilters(prev => {
      if (selectedDate && !prev.fechaDesde) {
        const previousDay = new Date(selectedDate);
        previousDay.setDate(previousDay.getDate() - 1);
        return { ...prev, fechaHasta: selectedDate, fechaDesde: previousDay };
      }
      return { ...prev, fechaHasta: selectedDate };
    });
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdropClassName="modal-backdrop"
      backdrop="static"
      className="modal-container small"
      style={{ zIndex: 1050 }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Filtros de Búsqueda</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <SoftBox mt={2}>
          <Grid container spacing={3}>
            {/* Cliente: oculto si es rol client */}
            {!isClient && (
              <Grid item xs={12} md={6}>
                <SoftBox display="flex" flexDirection="column" gap={1}>
                  <SoftBox>
                    <SoftTypography
                      variant="caption"
                      fontWeight="bold"
                      color="text"
                    >
                      Cliente
                    </SoftTypography>
                    <SoftSelect
                      options={clienteOptions}
                      value={filters.cliente}
                      onChange={selected =>
                        setFilters(prev => ({ ...prev, cliente: selected }))
                      }
                      placeholder="Seleccionar cliente..."
                      isClearable
                    />
                  </SoftBox>
                  <SoftTypography variant="caption" color="dark">
                    <p>Selecciona el cliente para filtrar las cobranzas asociadas.</p>
                  </SoftTypography>
                </SoftBox>
              </Grid>
            )}

            <Grid item xs={12} md={isClient ? 6 : 6}>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <SoftTypography
                    variant="caption"
                    fontWeight="bold"
                    color="text"
                  >
                    Tipo de Moneda
                  </SoftTypography>
                  <SoftSelect
                    options={monedaOptions}
                    value={filters.moneda}
                    onChange={selected =>
                      setFilters(prev => ({ ...prev, moneda: selected }))
                    }
                    placeholder="Seleccionar moneda..."
                    isClearable
                  />
                </SoftBox>
                <SoftTypography variant="caption" color="dark">
                  <p>Selecciona el tipo de moneda para filtrar las cobranzas.</p>
                </SoftTypography>
              </SoftBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <SoftTypography
                    variant="caption"
                    fontWeight="bold"
                    color="text"
                  >
                    Fecha Desde
                  </SoftTypography>
                  <SoftDatePicker
                    value={filters.fechaDesde}
                    onChange={handleFechaDesdeChange}
                    options={{
                      dateFormat: 'Y-m-d',
                      altInput: true,
                      altFormat: 'F j, Y',
                    }}
                  />
                </SoftBox>
                <SoftTypography variant="caption" color="dark">
                  <p>Selecciona la fecha inicial del rango para filtrar las cobranzas.</p>
                </SoftTypography>
              </SoftBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <SoftTypography
                    variant="caption"
                    fontWeight="bold"
                    color="text"
                  >
                    Fecha Hasta
                  </SoftTypography>
                  <SoftDatePicker
                    value={filters.fechaHasta}
                    onChange={handleFechaHastaChange}
                    options={{
                      dateFormat: 'Y-m-d',
                      altInput: true,
                      altFormat: 'F j, Y',
                    }}
                  />
                </SoftBox>
                <SoftTypography variant="caption" color="dark">
                  <p>Selecciona la fecha final del rango para filtrar las cobranzas.</p>
                </SoftTypography>
              </SoftBox>
            </Grid>
            {!isClient && (
                <Grid item xs={12} md={6}>
                <SoftBox display="flex" flexDirection="column" gap={1}>
                    <SoftBox>
                    <SoftTypography
                        variant="caption"
                        fontWeight="bold"
                        color="text"
                    >
                        Estado
                    </SoftTypography>
                    <SoftSelect
                        options={estadoOptions}
                        value={filters.estado}
                        onChange={selected =>
                        setFilters(prev => ({ ...prev, estado: selected }))
                        }
                        placeholder="Seleccionar estado..."
                        isClearable
                    />
                    </SoftBox>
                    <SoftTypography variant="caption" color="dark">
                    <p>Selecciona el estado de la cobranza para filtrar.</p>
                    </SoftTypography>
                </SoftBox>
                </Grid>
            )}
          </Grid>
        </SoftBox>
      </Modal.Body>
      <Modal.Footer>
        <SoftButton
          variant="gradient"
          color="secondary"
          onClick={handleReset}
          sx={{ mr: 2 }}
        >
          Limpiar
        </SoftButton>
        <SoftButton
          variant="gradient"
          color="success"
          onClick={handleApplyFilters}
          sx={{ mr: 2 }}
        >
          Aplicar Filtros
        </SoftButton>
        <SoftButton variant="gradient" color="error" onClick={handleClose}>
          Cancelar
        </SoftButton>
      </Modal.Footer>
    </Modal>
  );
}

ModalFiltros.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onApplyFilters: PropTypes.func.isRequired,
};

export default ModalFiltros;
